import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Alert, CircularProgress
} from '@mui/material';
import { format, addDays, parseISO } from 'date-fns';
import api from '../services/api';

const TITLES = {
  print:      'Print schedule',
  delete:     'Delete schedule',
  copy:       'Copy schedule',
  visibility: 'Employee visibility'
};

const fmtRange = (iso) => {
  if (!iso) return '—';
  try {
    const start = parseISO(iso);
    return `${format(start, 'MMM d')} — ${format(addDays(start, 6), 'MMM d, yyyy')}`;
  } catch {
    return iso;
  }
};

const WeekSelect = ({ label, value, onChange, weeks, currentWeek }) => (
  <TextField
    select
    SelectProps={{ native: true }}
    size="small"
    fullWidth
    label={label}
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    sx={{ mt: 2 }}
  >
    {weeks.map((w) => (
      <option key={w} value={w}>
        {fmtRange(w)}{w === currentWeek ? ' (current)' : ''}
      </option>
    ))}
  </TextField>
);

const ScheduleWeekActionModal = ({
  open, action, weeks, currentWeek, defaultWeek, onClose, onPrint, onDone
}) => {
  const [week, setWeek] = useState(defaultWeek);
  const [targetWeek, setTargetWeek] = useState('');
  const [released, setReleased] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Lista de destinos da cópia: as semanas conhecidas menos a de origem.
  const targetOptions = useMemo(
    () => weeks.filter((w) => w !== week),
    [weeks, week]
  );

  useEffect(() => {
    if (!open) return;
    setWeek(defaultWeek);
    setError('');
    setReleased(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultWeek]);

  useEffect(() => {
    if (!week) return;
    // destino default da cópia = semana seguinte à origem
    try {
      setTargetWeek(format(addDays(parseISO(week), 7), 'yyyy-MM-dd'));
    } catch {
      setTargetWeek('');
    }
  }, [week]);

  useEffect(() => {
    if (!open || action !== 'visibility' || !week) return;
    let alive = true;
    setReleased(null);
    api.get('/schedule/week/visibility', { params: { week_start: week } })
      .then((r) => { if (alive) setReleased(!!r.data?.released); })
      .catch(() => { if (alive) setError('Could not read visibility for this week.'); });
    return () => { alive = false; };
  }, [open, action, week]);

  const run = async () => {
    setBusy(true);
    setError('');
    try {
      if (action === 'print') {
        onPrint(week);
        onClose();
        return;
      }
      if (action === 'delete') {
        await api.delete('/schedule/clear-all', { params: { week_start: week } });
        alert(`✅ Schedule of ${fmtRange(week)} deleted.`);
      }
      if (action === 'copy') {
        await api.post('/schedule/copy-current-to-next-week', {
          week_start: week,
          target_week_start: targetWeek
        });
        alert(`✅ ${fmtRange(week)} copied to ${fmtRange(targetWeek)}.`);
      }
      if (action === 'visibility') {
        await api.put('/schedule/week/visibility', { week_start: week, released: !released });
        alert(!released
          ? `✅ ${fmtRange(week)} released — employees can see it now.`
          : `🔒 ${fmtRange(week)} hidden from employees.`);
      }
      onDone?.(action, week);
      onClose();
    } catch (e) {
      console.error('Schedule action failed:', e);
      setError(e?.response?.data?.msg || 'Action failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const confirmLabel = {
    print: 'Print',
    delete: 'Delete week',
    copy: 'Copy',
    visibility: released ? 'Hide from employees' : 'Release to employees'
  }[action];

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{TITLES[action] || 'Schedule'}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary">
          Which schedule week?
        </Typography>

        <WeekSelect
          label={action === 'copy' ? 'From week' : 'Week'}
          value={week}
          onChange={setWeek}
          weeks={weeks}
          currentWeek={currentWeek}
        />

        {action === 'copy' && (
          <>
            <WeekSelect
              label="To week"
              value={targetWeek}
              onChange={setTargetWeek}
              weeks={targetOptions}
              currentWeek={currentWeek}
            />
            <Alert severity="warning" sx={{ mt: 2 }}>
              Everything already in <strong>{fmtRange(targetWeek)}</strong> will be overwritten.
            </Alert>
          </>
        )}

        {action === 'delete' && (
          <Alert severity="error" sx={{ mt: 2 }}>
            This deletes every shift of <strong>{fmtRange(week)}</strong>. It cannot be undone.
          </Alert>
        )}

        {action === 'visibility' && (
          <Box sx={{ mt: 2 }}>
            {released === null && !error ? (
              <CircularProgress size={20} />
            ) : (
              <Alert severity={released ? 'success' : 'info'}>
                {released
                  ? 'Employees can see this week on their dashboard.'
                  : 'Hidden — employees see no schedule for this week until you release it.'}
              </Alert>
            )}
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color={action === 'delete' ? 'error' : 'primary'}
          onClick={run}
          disabled={busy || !week || (action === 'copy' && !targetWeek) || (action === 'visibility' && released === null)}
        >
          {busy ? <CircularProgress size={18} color="inherit" /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleWeekActionModal;
